

import { useState, useMemo, useEffect } from "react";
import { mastersApi, vendorsApi, clientsApi, quotationsApi, quotationSettingsApi } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import AddServiceModal from "./AddServiceModal";
import AddVendorModal from "./AddVendorModal";
import BulkAddServicesModal from "./BulkAddServicesModal";
import AddClientModal from "./AddClientModal";
import VenueModal from "./VenueModal";
import DatePickerWithAvailability from "../components/DatePickerWithAvailability";
import QuotationBasicInfo from "./QuotationBasicInfo";
import QuotationServicesTable from "./QuotationServicesTable";
import QuotationVendorsTable from "./QuotationVendorsTable";
import QuotationFinancials from "./QuotationFinancials";
import QuotationAdditionalDetails from "./QuotationAdditionalDetails";

interface Service {
  id: number;
  name: string;
  selling_price: number;
  base_price?: number;
  cost_price?: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
}

interface Vendor {
  id: number;
  name: string;
  commission_type?: string;
  commission_value?: number;
}

interface Package {
  id: number;
  name: string;
  base_price: number;
  offered_price?: number;
  services_data?: Service[];
  service_ids?: number[];
  vendors_data?: VendorItem[];
}

interface QuotationItem {
  id?: number;
  service_id?: number;
  description?: string;
  vendor_id?: number;
  quantity: number;
  unit_price: number;
  cost_price?: number;
  notes?: string;
}

interface VendorItem {
  id?: number;
  vendor_id?: number;
  description?: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  cost_price?: number;
  commission_percent?: number;
  commission_type?: string;
  notes?: string;
}

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface Venue {
  id: number;
  name: string;
  location?: string;
  capacity?: number;
  price_per_plate?: number;
}

interface QuotationFormData {
  id?: number;
  quotation_number?: string;
  client_id: number;
  client_name: string;
  event_name: string;
  event_type: string;
  guests: string;
  event_date: string;
  package_id?: number;
  venue: string;
  venue_id?: number;
  services_data: QuotationItem[];
  vendors_data: VendorItem[];
  discount_type: 'fixed' | 'percentage';
  discount_value: string;
  tax: string;
  is_taxable: boolean;
  notes: string;
  valid_until: string;
  status: string;
  subtotal?: number;
  total_amount?: number;
}

interface FullScreenQuotationModalProps {
  isOpen: boolean;
  initialData?: Partial<QuotationFormData>;
  onSubmit: (data: QuotationFormData) => void;
  onCancel: () => void;
}

const venues = ["Grand Ballroom", "Crystal Hall", "Garden Terrace", "Executive Suite A", "Executive Suite B"];

export default function FullScreenQuotationModal({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
}: FullScreenQuotationModalProps) {
  const { settings } = useSettings();
  const [packages, setPackages] = useState<Package[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [venuesList, setVenuesList] = useState<Venue[]>([]);
  const [eventTypeOptions, setEventTypeOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Resolved tax from settings — used both as initial value and in the sync effect
  const resolvedDefaultTax = (): string => {
    const v = settings?.general?.taxRate;
    // settings context has loaded a real value (may be "0", "5", "18", etc.)
    if (v !== undefined && v !== null) return String(v);
    return '0';
  };

  // Calculate default expiry date based on settings
  const getDefaultExpiryDate = (): string => {
    let validityDays = parseInt(settings?.general?.quotationValidityDays ?? '') || 0;
    if (!validityDays) {
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('settings') : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          validityDays = parseInt(parsed?.general?.quotationValidityDays) || 7;
        }
      } catch { /* ignore */ }
    }
    if (!validityDays) validityDays = 7;
    const date = new Date();
    date.setDate(date.getDate() + validityDays);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const [formData, setFormData] = useState<QuotationFormData>({
    id: initialData?.id,
    quotation_number: initialData?.quotation_number,
    client_id: initialData?.client_id || 0,
    client_name: initialData?.client_name || "",
    event_name: initialData?.event_name || "",
    event_type: initialData?.event_type || "",
    guests: initialData?.guests || "",
    event_date: initialData?.event_date || "",
    package_id: initialData?.package_id,
    venue: initialData?.venue || "",
    venue_id: initialData?.venue_id,
    services_data: initialData?.services_data || [],
    vendors_data: initialData?.vendors_data || [],
    discount_type: initialData?.discount_type || "fixed",
    discount_value: initialData?.discount_value || "0",
    tax: initialData?.tax !== undefined ? String(initialData.tax) : resolvedDefaultTax(),
    is_taxable: initialData?.is_taxable !== undefined ? initialData.is_taxable : true,
    notes: initialData?.notes || "",
    valid_until: initialData?.valid_until || getDefaultExpiryDate(),
    status: initialData?.status || "draft",
    subtotal: initialData?.subtotal || 0,
    total_amount: initialData?.total_amount || 0,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof QuotationFormData, string>>>({});
  const [customOptions, setCustomOptions] = useState({
    venues: [...venues],
  });

  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);
  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isAddVenueModalOpen, setIsAddVenueModalOpen] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null);
  const [editingVendorIndex, setEditingVendorIndex] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // When settings finish loading, push the real taxRate into new-quotation forms.
  // "New" = no initialData.id (create mode), and the tax still matches the stale default.
  useEffect(() => {
    if (!isOpen) return;
    if (initialData?.id) return; // editing an existing quotation — don't overwrite
    const realTax = resolvedDefaultTax();
    setFormData(prev => ({ ...prev, tax: realTax }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, isOpen]);

  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        id: initialData.id,
        quotation_number: initialData.quotation_number,
        client_id: initialData.client_id || 0,
        client_name: initialData.client_name || "",
        event_name: initialData.event_name || "",
        event_type: initialData.event_type || "",
        guests: initialData.guests || "",
        event_date: initialData.event_date || "",
        package_id: initialData.package_id,
        venue: initialData.venue || "",
        venue_id: initialData.venue_id,
        services_data: initialData.services_data || [],
        vendors_data: initialData.vendors_data || [],
        discount_type: initialData.discount_type || "fixed",
        discount_value: initialData.discount_value || "0",
        tax: initialData.tax !== undefined ? String(initialData.tax) : resolvedDefaultTax(),
        is_taxable: initialData.is_taxable !== undefined ? initialData.is_taxable : true,
        notes: initialData.notes || "",
        valid_until: initialData.valid_until || getDefaultExpiryDate(),
        status: initialData.status || "draft",
        subtotal: initialData.subtotal || 0,
        total_amount: initialData.total_amount || 0,
      });
      setHasChanges(false);
      setErrors({});
    }
  }, [initialData, isOpen]);

  const generateQuotationNumber = (quotations: any[]) => {
    if (!quotations || quotations.length === 0) {
      return "QT-001";
    }
    
    // Extract numbers from quotation_number field
    const numbers = quotations
      .map(q => {
        const match = q.quotation_number?.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      })
      .filter(n => n > 0)
      .sort((a, b) => b - a);
    
    const lastNumber = numbers[0] || 0;
    const nextNumber = lastNumber + 1;
    return `QT-${String(nextNumber).padStart(3, "0")}`;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [packagesRes, servicesRes, vendorsRes, clientsRes, quotationsRes, venuesRes, eventTypesRes, settingsRes] = await Promise.all([
        mastersApi.packages.list(),
        mastersApi.services.list(),
        vendorsApi.list(),
        clientsApi.list(),
        quotationsApi.list(),
        mastersApi.venues.list(),
        mastersApi.eventTypes.list({ status: 'active' }),
        quotationSettingsApi.get(),
      ]);
      setPackages(packagesRes.data?.data || packagesRes.data || []);
      setServices(servicesRes.data?.data || servicesRes.data || []);
      setVendors(vendorsRes.data?.data || vendorsRes.data || []);
      setClients(clientsRes.data?.data || clientsRes.data || []);
      setVenuesList(venuesRes.data?.data || venuesRes.data || []);
      const etData = eventTypesRes.data?.data || eventTypesRes.data || [];
      setEventTypeOptions(etData.map((t: any) => t.name));
      
      // Auto-generate quotation number if creating new quotation using settings
      if (!initialData?.id) {
        const settings = settingsRes.data?.data || settingsRes.data;
        const preview = settings?.preview || settings?.next_number;
        if (preview) {
          setFormData(prev => ({ ...prev, quotation_number: preview }));
        }
      }
    } catch (error) {
      console.error("Failed to load data", error);
      setEventTypeOptions(["Wedding", "Corporate", "Birthday", "Social", "Festival"]);
    } finally {
      setLoading(false);
    }
  };

  const calculatedValues = useMemo(() => {
    const servicesTotal = formData.services_data.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const vendorsTotal = formData.vendors_data.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const subtotal = servicesTotal + vendorsTotal;

    const discountValue = parseFloat(formData.discount_value) || 0;
    const discount = formData.discount_type === 'percentage'
      ? subtotal * (discountValue / 100)
      : discountValue;
    const afterDiscount = Math.max(0, subtotal - discount);

    const taxPercent = formData.is_taxable ? (parseFloat(formData.tax) || 0) : 0;
    const taxAmount = afterDiscount * (taxPercent / 100);
    const total = afterDiscount + taxAmount;

    return { subtotal, discount, taxAmount, total, servicesTotal, vendorsTotal };
  }, [formData.services_data, formData.vendors_data, formData.discount_type, formData.discount_value, formData.tax, formData.is_taxable]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof QuotationFormData, string>> = {};
    
    if (!formData.client_id || formData.client_id === 0) newErrors.client_id = "Client is required";
    if (!formData.client_name.trim()) newErrors.client_name = "Client name is required";
    if (!formData.event_name.trim()) newErrors.event_name = "Event name is required";
    if (!formData.event_type) newErrors.event_type = "Event type is required";
    if (!formData.guests || parseInt(formData.guests) <= 0) newErrors.guests = "Valid guest count is required";
    if (!formData.event_date) newErrors.event_date = "Event date is required";
    if (formData.services_data.length === 0 && formData.vendors_data.length === 0) {
      newErrors.services_data = "At least one service or vendor is required";
    }
    if (!formData.valid_until) newErrors.valid_until = "Valid until date is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof QuotationFormData, value: any) => {
    let updatedData = { [field]: value };

    // Auto-generate event_name from client name and event_type if event_name is empty
    if ((field === "event_type" || field === "client_name") && !formData.event_name.trim()) {
      const eventType = field === "event_type" ? value : formData.event_type;
      const clientName = field === "client_name" ? value : formData.client_name;

      if (eventType && clientName) {
        updatedData.event_name = `${clientName} - ${eventType}`;
      }
    }

    setFormData(prev => ({ ...prev, ...updatedData }));
    setHasChanges(true);
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCloseWithConfirmation = () => {
    if (hasChanges) {
      setIsCloseConfirmOpen(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmClose = () => {
    setIsCloseConfirmOpen(false);
    onCancel();
  };

  const handleAddService = (newService: QuotationItem) => {
    if (editingServiceIndex !== null) {
      const updated = [...formData.services_data];
      updated[editingServiceIndex] = newService;
      setFormData(prev => ({ ...prev, services_data: updated }));
      setEditingServiceIndex(null);
    } else {
      setFormData(prev => ({ ...prev, services_data: [...prev.services_data, newService] }));
    }
    setIsAddServiceModalOpen(false);
  };

  const handleBulkAddServices = (newServices: QuotationItem[]) => {
    setFormData(prev => ({ ...prev, services_data: [...prev.services_data, ...newServices] }));
    setIsBulkAddModalOpen(false);
  };

  const handleRemoveService = (index: number) => {
    setFormData(prev => ({
      ...prev,
      services_data: prev.services_data.filter((_, i) => i !== index),
    }));
  };

  const handleEditService = (index: number) => {
    setEditingServiceIndex(index);
    setIsAddServiceModalOpen(true);
  };

  const handleUpdateService = (index: number, field: keyof QuotationItem, value: any) => {
    setFormData(prev => {
      const updatedServices = [...prev.services_data];
      updatedServices[index] = { ...updatedServices[index], [field]: value };
      return { ...prev, services_data: updatedServices };
    });
  };

  const handleAddVendor = (newVendor: VendorItem) => {
    if (editingVendorIndex !== null) {
      const updated = [...formData.vendors_data];
      updated[editingVendorIndex] = newVendor;
      setFormData(prev => ({ ...prev, vendors_data: updated }));
      setEditingVendorIndex(null);
    } else {
      setFormData(prev => ({ ...prev, vendors_data: [...prev.vendors_data, newVendor] }));
    }
    setIsAddVendorModalOpen(false);
  };

  const handleRemoveVendor = (index: number) => {
    setFormData(prev => ({
      ...prev,
      vendors_data: prev.vendors_data.filter((_, i) => i !== index),
    }));
  };

  const handleClientCreated = (clientId: number, clientName: string) => {
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      client_name: clientName,
    }));
    setClients(prev => [...prev, { id: clientId, name: clientName }]);
    setIsAddClientModalOpen(false);
  };

  const handleVenueCreated = (venueId: number, venueName: string) => {
    setFormData(prev => ({
      ...prev,
      venue: venueName,
      venue_id: venueId,
    }));
    setVenuesList(prev => [...prev, { id: venueId, name: venueName }]);
    setIsAddVenueModalOpen(false);
    setHasChanges(true);
  };

  const handleEditVendor = (index: number) => {
    setEditingVendorIndex(index);
    setIsAddVendorModalOpen(true);
  };

  const handleUpdateVendor = (index: number, field: keyof VendorItem, value: any) => {
    setFormData(prev => {
      const updatedVendors = [...prev.vendors_data];
      updatedVendors[index] = { ...updatedVendors[index], [field]: value };
      return { ...prev, vendors_data: updatedVendors };
    });
  };

  const handleAddNew = (field: "venues", value: string) => {
    setCustomOptions(prev => ({
      ...prev,
      [field]: [...prev[field], value],
    }));
    const formField = "venue";
    handleChange(formField as keyof QuotationFormData, value);
  };

  const handleAddNewEventType = (value: string) => {
    setEventTypeOptions(prev => [...prev, value]);
    handleChange("event_type", value);
  };

  const handlePackageChange = (packageId: string) => {
    const selectedPackage = packages.find(p => String(p.id) === packageId);
    if (selectedPackage) {
      const packageServices: QuotationItem[] = [];
      
      if (selectedPackage.services_data && selectedPackage.services_data.length > 0) {
        selectedPackage.services_data.forEach((service: any) => {
          packageServices.push({
            service_id: service.service_id || service.id,
            description: service.description || service.name,
            vendor_id: service.vendor_id,
            quantity: service.quantity || 1,
            unit_price: service.unit_price || service.selling_price || 0,
            cost_price: service.cost_price || service.base_price || 0,
            notes: service.notes || "",
          });
        });
      } else if (selectedPackage.service_ids && selectedPackage.service_ids.length > 0) {
        selectedPackage.service_ids.forEach(svcId => {
          const service = services.find(s => s.id === svcId);
          if (service) {
            packageServices.push({
              service_id: service.id,
              description: service.name,
              vendor_id: service.vendor_id,
              quantity: 1,
              unit_price: service.selling_price || 0,
              cost_price: service.base_price || 0,
              notes: "",
            });
          }
        });
      }
      
      setFormData(prev => ({
        ...prev,
        package_id: selectedPackage.id,
        services_data: packageServices,
        // Keep existing vendors_data, don't add package vendors
        vendors_data: prev.vendors_data,
        total_amount: selectedPackage.offered_price || selectedPackage.base_price,
      }));
    } else {
      handleChange("package_id", packageId ? parseInt(packageId) : undefined);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-stone-50 to-white flex-shrink-0">
        <h2 className="text-lg sm:text-xl font-bold text-stone-800">
          {initialData ? "Edit Quotation" : "Create New Quotation"}
        </h2>
        <button 
          onClick={handleCloseWithConfirmation}
          className="w-8 h-8 rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700 flex items-center justify-center transition-colors flex-shrink-0"
          title="Close (Ctrl+W)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6 mx-auto">
            {/* Basic Info */}
            <QuotationBasicInfo
              quotationNumber={formData.quotation_number}
              clientId={formData.client_id}
              clientName={formData.client_name}
              eventName={formData.event_name}
              eventType={formData.event_type}
              guests={formData.guests}
              eventDate={formData.event_date}
              venue={formData.venue}
              venueId={formData.venue_id}
              packageId={formData.package_id}
              clients={clients}
              packages={packages}
              venues={venuesList}
              loading={loading}
              errors={errors}
              customOptions={customOptions}
              eventTypeOptions={eventTypeOptions}
              onQuotationNumberChange={(v) => handleChange("quotation_number", v)}
              onClientIdChange={(v) => handleChange("client_id", v)}
              onClientNameChange={(v) => handleChange("client_name", v)}
              onEventNameChange={(v) => handleChange("event_name", v)}
              onEventTypeChange={(v) => handleChange("event_type", v)}
              onGuestsChange={(v) => handleChange("guests", v)}
              onEventDateChange={(v) => handleChange("event_date", v)}
              onVenueChange={(v) => handleChange("venue", v)}
              onVenueIdChange={(v) => handleChange("venue_id", v)}
              onPackageChange={handlePackageChange}
              onAddNewEventType={(v) => handleAddNewEventType(v)}
              onAddNewVenue={() => setIsAddVenueModalOpen(true)}
              onAddNewClient={() => setIsAddClientModalOpen(true)}
            />

            {/* Services Table */}
            <QuotationServicesTable
              services={formData.services_data}
              vendors={vendors}
              onAddService={() => {
                setEditingServiceIndex(null);
                setIsAddServiceModalOpen(true);
              }}
              onEditService={handleEditService}
              onRemoveService={handleRemoveService}
              onUpdateService={handleUpdateService}
              onBulkAdd={() => setIsBulkAddModalOpen(true)}
            />

            {/* Vendors Table */}
            <QuotationVendorsTable
              vendors={formData.vendors_data}
              vendorList={vendors}
              onAddVendor={() => {
                setEditingVendorIndex(null);
                setIsAddVendorModalOpen(true);
              }}
              onEditVendor={handleEditVendor}
              onRemoveVendor={handleRemoveVendor}
              onUpdateVendor={handleUpdateVendor}
            />

            {/* Financials */}
            <QuotationFinancials
              discountType={formData.discount_type}
              discountValue={formData.discount_value}
              tax={formData.tax}
              isTaxable={formData.is_taxable}
              validUntil={formData.valid_until}
              calculatedValues={calculatedValues}
              errors={errors}
              onDiscountTypeChange={(v) => handleChange("discount_type", v)}
              onDiscountValueChange={(v) => handleChange("discount_value", v)}
              onTaxChange={(v) => handleChange("tax", v)}
              onIsTaxableChange={(v) => handleChange("is_taxable", v)}
              onValidUntilChange={(v) => handleChange("valid_until", v)}
            />

            {/* Additional Details */}
            <QuotationAdditionalDetails
              status={formData.status}
              notes={formData.notes}
              onStatusChange={(v) => handleChange("status", v)}
              onNotesChange={(v) => handleChange("notes", v)}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-4 sm:px-6 py-3 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 rounded-lg text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
          >
            {initialData ? "Update Quotation" : "Create Quotation"}
          </button>
        </div>

      {/* Close Confirmation Dialog */}
      {isCloseConfirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-bold text-stone-800 mb-2">Discard Changes?</h3>
            <p className="text-sm text-stone-600 mb-6">
              You have unsaved changes. Are you sure you want to close without saving?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setIsCloseConfirmOpen(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-stone-200 text-stone-700 font-medium hover:bg-stone-50 transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={handleConfirmClose}
                className="flex-1 px-4 py-2 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700 transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Service Modal */}
      <AddServiceModal
        isOpen={isAddServiceModalOpen}
        onClose={() => {
          setIsAddServiceModalOpen(false);
          setEditingServiceIndex(null);
        }}
        onAdd={handleAddService}
        services={services}
        vendors={vendors}
        initialData={editingServiceIndex !== null ? formData.services_data[editingServiceIndex] : undefined}
      />

      {/* Bulk Add Services Modal */}
      <BulkAddServicesModal
        isOpen={isBulkAddModalOpen}
        onClose={() => setIsBulkAddModalOpen(false)}
        onAdd={handleBulkAddServices}
        services={services}
        vendors={vendors}
      />

      {/* Add Vendor Modal */}
      <AddVendorModal
        isOpen={isAddVendorModalOpen}
        onClose={() => {
          setIsAddVendorModalOpen(false);
          setEditingVendorIndex(null);
        }}
        onAdd={handleAddVendor}
        vendors={vendors}
        initialData={editingVendorIndex !== null ? formData.vendors_data[editingVendorIndex] : undefined}
      />

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onClientCreated={handleClientCreated}
      />

      {/* Add Venue Modal */}
      <VenueModal
        isOpen={isAddVenueModalOpen}
        onClose={() => setIsAddVenueModalOpen(false)}
        onVenueCreated={handleVenueCreated}
      />
    </div>
  );
}
